/**
 * Authenticated file downloads.
 *
 * Exports are served by DRF behind the same JWT + `X-School` gate as every
 * other endpoint, so a plain `window.location = …` navigation 401s (the access
 * token lives in memory, F6). The bytes therefore come back through the axios
 * client as a blob and the save is triggered from an object URL.
 *
 * Adapted from the template's `csvExport.ts` (dev13-spa) — no `file-saver`
 * dependency: an anchor with `download` is all a save needs.
 */
import { apiClient } from './client';

/** Pull the filename out of a `Content-Disposition` header (RFC 5987 aware). */
export function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  const match = (disposition ?? '').match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Hand a blob to the browser as a save, under `filename`. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * `GET` a binary endpoint through the authenticated client and save it.
 *
 * @param path         API path relative to the client baseURL, e.g. `/gradebook/export/`.
 * @param params       Query params (class, window, `format`).
 * @param fallbackName Saved-as name when the server sends no `Content-Disposition`.
 */
export async function downloadFile(
  path: string,
  params: Record<string, unknown> = {},
  fallbackName = 'download'
): Promise<void> {
  const { data, headers } = await apiClient.get<Blob>(path, { params, responseType: 'blob' });
  const disposition = (headers as Record<string, unknown>)['content-disposition'];
  const filename = filenameFromDisposition(
    typeof disposition === 'string' ? disposition : undefined,
    fallbackName
  );
  saveBlob(data, filename);
}
